import type { CompressFallbackReason } from '@paintscout/util/s3'

/**
 * Overall ceiling for a single file's compress + upload, shared by both editor
 * variants. Belt-and-suspenders on top of the per-step timeouts in
 * compressFile/s3UploadFile: even an unforeseen hung step (e.g. the
 * getUploadUrl/processFile GraphQL calls, which have no client-side timeout)
 * can't strand the uploading flag and dead-lock the "Done" button on iPad
 * Chrome (#21104). On reject, the dialog's uploadFiles catch clears the flag and
 * surfaces a retry, so "Done" re-enables.
 *
 * Must stay larger than the per-step timeouts it backstops — currently the 8s
 * compress (compressFile.ts) + 60s S3 PUT (s3UploadFile.ts) = 68s, leaving a 22s
 * buffer — so a slow-but-progressing upload isn't killed before its own step
 * timeout fires. If you change any of the three, keep this the largest.
 */
export const UPLOAD_TIMEOUT_MS = 90000

export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  // Assigned synchronously inside the Promise executor below (executors run
  // synchronously), so it's always set before .finally references it.
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
}

/**
 * Stamp the compress-fallback reason onto an upload error (without clobbering a
 * more specific value) so the failure diagnostic can report whether image
 * compression fell back to the original beforehand — a prime iOS suspect (#21104).
 */
export function stampCompressFallback(error: unknown, compressFallback?: CompressFallbackReason): void {
  if (
    compressFallback &&
    error &&
    typeof error === 'object' &&
    !(error as { compressFallback?: unknown }).compressFallback
  ) {
    ;(error as { compressFallback?: CompressFallbackReason }).compressFallback = compressFallback
  }
}
